import { Cannon, STARTING_CANNONS } from "../../../shared/Gunnery";
import { NAME_MAX_LENGTH } from "../data/Player";

/** Prefix of the name shown until there is a stored `displayName` to read. See design/AccountData.md. */
const DEFAULT_USER = "Guest";

/** Digits appended to `Guest`, so two unnamed players on the same sea are still distinguishable. */
const GUEST_SUFFIX_DIGITS = 6;

/**
 * `Guest-482913`. Cosmetic only — it is not an id and nothing is keyed on it, so a
 * collision between two guests costs nothing but a repeated label.
 */
function guestName(): string {
    const max = 10 ** GUEST_SUFFIX_DIGITS;
    const suffix = Math.floor(Math.random() * max)
        .toString()
        .padStart(GUEST_SUFFIX_DIGITS, "0");

    return `${DEFAULT_USER}-${suffix}`;
}

/** The button's two states. It is the only way in and out of an arena. */
const LABEL_JOIN = "Battle";
const LABEL_LEAVE = "Leave";

/**
 * The glyph on a gun's cell, rotated to the bearing that gun rests at, with the bow at the top.
 * The row is therefore the deck plan seen from above: the shipped hull 1 is one arrow pointing
 * up — a single chase gun — and the stern slot hull 2 adds appears beside it pointing down.
 */
const GUN_GLYPH = "▲";

/**
 * One gun's chrome: the cell and the bar that fills over its reload. Keyed by cannon slot,
 * because a slot is a gun's identity across a refit — see `Cannon.index` in shared/Gunnery.ts.
 */
interface GunCell {
    cell: HTMLElement;
    bar: HTMLElement;
}

/** A gun that has been fired, and the clock it comes back up on. Keyed by its cannon slot. */
interface Reload {
    timer: ReturnType<typeof setTimeout>;
    /** `performance.now()` at which this gun is loaded again. */
    readyAt: number;
}

/**
 * The header bar across the top of the game. Real DOM, not Pixi: it is app chrome rather
 * than part of the sea, so it wants text selection, focus rings and button semantics for
 * free — see design/UI.md.
 *
 * **The guns are a readout, not a control.** They used to be four buttons, one per side, each
 * with a key under it; aiming is the pointer now and the gun a press fires is chosen by the
 * ship rather than by the player (design/Controls.md), so a button per side would be a control
 * for a decision nobody makes any more. What is left is the thing a captain actually reads
 * mid-fight: how many barrels this hull carries, which way each one is bolted, and which of
 * them are loaded right now.
 *
 * Styles live in the `<style>` block of `src/index.html`; there is no CSS pipeline.
 */
export class Toolbar {
    private readonly _root = document.createElement("header");
    /**
     * The captain's name, and the one piece of the header a player can **type into**.
     *
     * A real `<input>` rather than a span, which is the whole reason design/UI.md puts chrome in
     * the DOM: focus, selection, IME and a caret all come free, and none of them is worth
     * rebuilding in Pixi for a field somebody edits once.
     *
     * It starts on a guest label and shows a stored `displayName` when there is one. The label is
     * a *display* of that field rather than a second source of truth for it — the store is where
     * it lives, and this is what shows it. See design/AccountData.md.
     */
    private readonly _user = document.createElement("input");
    private readonly _guns = document.createElement("div");
    /** The chrome for each gun the ship carries, keyed by its cannon slot. */
    private readonly _cells = new Map<number, GunCell>();
    /**
     * The way into the shop, and **ashore only**: there is nothing to buy mid-battle, and a button
     * that opened a storefront over a live sea would be a button that gets somebody sunk.
     */
    private readonly _shop = document.createElement("button");
    private readonly _battle = document.createElement("button");
    /**
     * The doubloon count, left of the Battle button. One readout in two moods: what this run has
     * earned while there is a run, and what is in the wallet ashore.
     *
     * Ashore it currently shows what the **last** run earned and nothing is kept — there is no
     * wallet yet (design/Shop.md), and a zero that reset on every reload would be a worse lie than
     * a number that at least means something. It becomes the stored balance in the phase that
     * builds the locker.
     */
    private readonly _purse = document.createElement("span");
    /** Reloads under way by cannon slot, so leaving an arena does not leave a cell stuck down. */
    private readonly _reloading = new Map<number, Reload>();
    /** The guns the ship is actually carrying, in slot order. */
    private _battery: readonly Cannon[] = STARTING_CANNONS;

    private _inBattle = false;

    /**
     * Called when the button is pressed. The toolbar does not know what an arena is —
     * whoever owns the scenes decides what joining and leaving mean.
     */
    public onBattle: (() => void) | null = null;

    /** The name shown on the left. Defaults to `Guest-` plus six random digits. */
    public get user(): string {
        return this._user.value;
    }
    public set user(name: string) {
        this._user.value = name;
    }

    /**
     * Called when the player has finished typing a name — on blur, or on Enter.
     *
     * **Not on every keystroke.** Writes happen on meaningful change, never per input event
     * (design/AccountData.md), and half a name is not a name. Whoever owns the store decides what
     * to do with it; the bar has no idea one exists.
     */
    public onRename: ((name: string) => void) | null = null;

    /** Called when the Shop button is pressed. The bar has no idea what a shop is. */
    public onShop: (() => void) | null = null;

    /** Flips the button between joining and leaving. Set by the owner, not by the click. */
    public get inBattle(): boolean {
        return this._inBattle;
    }

    public set inBattle(value: boolean) {
        this._inBattle = value;
        this._battle.textContent = value ? LABEL_LEAVE : LABEL_JOIN;
        // Hidden at sea rather than disabled: a dead button is chrome that lies about what the
        // player can do, which is the same rule the battery row follows ashore.
        this._shop.hidden = value;
        // Guns are for a sea with ships in it. Hidden rather than dimmed ashore, because a row
        // of dead cells over the attract screen is chrome that describes nothing.
        this._guns.hidden = !value;
        // A battle begins on the boat the dashboard authored, so the header goes back to that
        // boat's battery — whatever a previous life had grown into. See design/Evolution.md.
        if (value) this.battery = STARTING_CANNONS;
        else this._unloadGuns();
    }

    /**
     * The guns the ship is carrying right now, in cannon-slot order — one cell each, turned to
     * the bearing that gun rests at.
     *
     * It is written on every upgrade that touches the battery (design/Evolution.md). A cell
     * appearing is how the player learns a rail has been filled, and the row growing is how they
     * learn the hull did. The row is rebuilt rather than patched, because a hull upgrade can
     * change how many slots there are, where they point and what is in them all at once — but a
     * gun that is **mid-reload keeps reloading** across that rebuild, since the shot it is
     * loading was really fired.
     *
     * The arrow is the *rest* bearing and not where the barrel is currently pointing. A gun
     * swings now, and a row of arrows twitching with the pointer would be a second, worse copy
     * of the picture already on the water — where the guns themselves are drawn turning. What
     * the row is for is the arcs the deck plan gives this ship, which is a thing that only
     * changes when a point is spent. See design/Controls.md.
     */
    /**
     * What this life has earned, in doubloons — `Ship.purse` off the wire, written every frame
     * because it changes once a second and the difference is not worth a second clock.
     *
     * Null takes the readout down entirely, which is what an arena with no ship in it looks like.
     * The text is compared before it is assigned: a `<span>` whose string is reassigned to the
     * same value still costs a layout in some browsers, and this is chrome that must not.
     */
    public set purse(value: number | null) {
        this._purse.hidden = value === null;
        if (value === null) return;

        const text = `${Math.floor(value).toLocaleString()} dbl`;
        if (this._purse.textContent !== text) this._purse.textContent = text;
    }

    public get battery(): readonly Cannon[] {
        return this._battery;
    }

    public set battery(cannons: readonly Cannon[]) {
        this._battery = cannons;
        // A slot that is no longer on the boat has no cell to bring back up, and its timer would
        // refresh chrome that is not in the row any more. A hull upgrade is the case.
        for (const [index, reload] of this._reloading) {
            if (cannons.some((cannon) => cannon.index === index)) continue;
            clearTimeout(reload.timer);
            this._reloading.delete(index);
        }

        this._cells.clear();
        this._guns.replaceChildren();
        for (const cannon of cannons) this._guns.appendChild(this._buildGun(cannon));
        for (const cannon of cannons) this._refresh(cannon.index);
    }

    /**
     * Marks one gun as just fired and runs its reload down.
     *
     * The clock is the **client's**, like the trigger that picked the gun and like the turbo
     * prediction: a readout that waited for the arena to confirm a shot would lag behind what
     * the player can actually do next. The server keeps its own copy of the same reload and
     * declines anything early, so the worst a disagreement costs is a press that produces no
     * ball. See design/Networking.md.
     */
    public fired(cannon: Cannon): void {
        const index = cannon.index;
        const reloadMs = cannon.reload * 1000;

        clearTimeout(this._reloading.get(index)?.timer);
        this._reloading.set(index, {
            readyAt: performance.now() + reloadMs,
            timer: setTimeout(() => {
                this._reloading.delete(index);
                this._refresh(index);
            }, reloadMs)
        });

        this._refresh(index);
    }

    constructor() {
        this._root.className = "toolbar";

        this._user.className = "toolbar-user";
        this._user.type = "text";
        this._user.maxLength = NAME_MAX_LENGTH;
        this._user.spellcheck = false;
        this._user.setAttribute("aria-label", "Your captain's name");
        this._user.value = guestName();

        // Blur and Enter, and nothing in between: a name is worth storing when somebody has
        // finished typing it. Enter also drops focus, so the next key press goes to the ship
        // rather than into the box — every key the game binds is listened for on `window`
        // (design/Controls.md), and a focused input would otherwise swallow the helm.
        this._user.addEventListener("blur", () => this.onRename?.(this._user.value));
        this._user.addEventListener("keydown", (event) => {
            if (event.key === "Enter") this._user.blur();
            // Steering is `A`/`D` on `window`. Without this, typing a name sails the ship.
            event.stopPropagation();
        });

        // One cell per gun the boat tree's fresh hull carries — two, for a boat whose only guns
        // are on the rails. An unarmed tree is a bar with no cells on it rather than a special
        // case: the row is exactly as long as the roster.
        this._guns.className = "toolbar-guns";
        this._guns.hidden = true;
        for (const cannon of STARTING_CANNONS) this._guns.appendChild(this._buildGun(cannon));
        for (const cannon of STARTING_CANNONS) this._refresh(cannon.index);

        // Cream on the bar's own wash rather than the action red: earning is not "this starts a
        // fight", and design/UI.md is explicit that the red is not spent on anything else.
        this._purse.className = "toolbar-purse";
        this._purse.hidden = true;

        this._shop.className = "toolbar-shop";
        this._shop.type = "button";
        this._shop.textContent = "Shop";
        this._shop.addEventListener("click", () => this.onShop?.());

        this._battle.className = "toolbar-battle";
        this._battle.type = "button";
        this._battle.textContent = LABEL_JOIN;
        this._battle.addEventListener("click", () => this.onBattle?.());

        this._root.append(this._user, this._guns, this._purse, this._shop, this._battle);
    }

    /** One gun's cell: an arrow turned to its slot, and a bar under it that runs its reload. */
    private _buildGun(cannon: Cannon): HTMLElement {
        const cell = document.createElement("span");
        cell.className = "toolbar-gun";
        // The hook the suite reads a gun by. A slot is this gun's identity across a refit.
        cell.dataset.slot = String(cannon.index);

        const arrow = document.createElement("span");
        arrow.className = "toolbar-gun-arrow";
        arrow.textContent = GUN_GLYPH;
        // The art faces north and so does a heading, so an untouched arrow is a bow chaser.
        arrow.style.transform = `rotate(${cannon.bearing}rad)`;

        const bar = document.createElement("span");
        bar.className = "toolbar-gun-bar";

        cell.append(arrow, bar);
        this._cells.set(cannon.index, { cell, bar });

        return cell;
    }

    /** Puts one gun's cell in step with its clock: lit while loaded, and dimmed while it is not. */
    private _refresh(index: number): void {
        const chrome = this._cells.get(index);
        if (!chrome) return;

        const reload = this._reloading.get(index);
        chrome.cell.classList.toggle("is-reloading", Boolean(reload));

        for (const animation of chrome.bar.getAnimations()) animation.cancel();
        if (!reload) return;

        // The Web Animations API rather than a CSS class: the duration is a tuning value out
        // of `config/boat.json`, and there is no CSS pipeline to hand it to a stylesheet.
        chrome.bar.animate([{ transform: "scaleX(0)" }, { transform: "scaleX(1)" }], {
            duration: Math.max(0, reload.readyAt - performance.now()),
            easing: "linear"
        });
    }

    /** Drops every pending reload and lets the cells back up. Leaving an arena unloads them. */
    private _unloadGuns(): void {
        for (const reload of this._reloading.values()) clearTimeout(reload.timer);
        this._reloading.clear();
        for (const index of this._cells.keys()) this._refresh(index);
    }

    public mount(parent: HTMLElement = document.body): void {
        parent.appendChild(this._root);
    }

    public destroy(): void {
        this._unloadGuns();
        this._root.remove();
    }
}
